/**
 * Admin-console API (v1-2-5). Staff-only operator endpoints, all behind real
 * staff JWT auth via `authedFetch` (which attaches the Bearer token and bounces
 * to /admin/login on 401). Login itself is the one un-authed call.
 */

import { ApiError, type CampaignRead } from './api';
import { authedFetch, getToken, setSession } from './auth';

export type { CampaignRead };

// --- login -------------------------------------------------------------------

export interface StaffLoginResponse {
  access_token: string;
  token_type: string;
  role: string;
}

/** Authenticate an operator and persist the session. Throws ApiError on 401. */
export async function staffLogin(
  email: string,
  password: string,
): Promise<StaffLoginResponse> {
  let resp: Response;
  try {
    resp = await fetch('/api/v1/auth/staff/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch (e) {
    throw new ApiError(
      'We could not reach the server. Please check your connection and try again.',
      0,
      e,
    );
  }
  if (!resp.ok) {
    const message =
      resp.status === 401
        ? 'Invalid email or password.'
        : resp.status === 503
          ? 'Authentication is not configured in this environment.'
          : 'Sign-in failed. Please try again.';
    throw new ApiError(message, resp.status);
  }
  const data = (await resp.json()) as StaffLoginResponse;
  setSession(data.access_token, data.role);
  return data;
}

// --- applications ------------------------------------------------------------

export interface ApplicationListItem {
  id: string;
  reference: string;
  company_name: string;
  industry: string;
  contact_name: string;
  email: string;
  prize_product: string;
  prize_value: string;
  sponsorship_type: string;
  status: string;
  created_at: string;
}

export interface ApplicationDetail extends ApplicationListItem {
  company_reg_no?: string | null;
  website: string;
  phone: string;
  campaign_objective: string;
  target_audience: string;
  preferred_dates: string;
  qr_placement: string;
  wants_leads: boolean;
  wants_ai_marketing: boolean;
  image_urls?: string[] | null;
  notes?: string | null;
  consent_acknowledged: boolean;
  consent_text: string;
  consent_at: string;
  converted_partner_id?: string | null;
  converted_campaign_id?: string | null;
}

/** Result of an application status transition. On approve, carries the new partner + campaign. */
export interface ApplicationTransitionResult {
  id: string;
  reference: string;
  status: string;
  converted_partner_id?: string | null;
  converted_partner_slug?: string | null;
  converted_campaign_id?: string | null;
  // On approve, the auto-provisioned partner login. temp_password shows ONCE
  // (null if a login for that email already existed).
  partner_login_email?: string | null;
  partner_temp_password?: string | null;
}

export function listApplications(status?: string): Promise<ApplicationListItem[]> {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return authedFetch<ApplicationListItem[]>(
    `/api/v1/admin/applications${q}`,
    { method: 'GET' },
    'Could not load applications.',
  );
}

export function getApplication(id: string): Promise<ApplicationDetail> {
  return authedFetch<ApplicationDetail>(
    `/api/v1/admin/applications/${encodeURIComponent(id)}`,
    { method: 'GET' },
    'Could not load this application.',
  );
}

/** Move a submitted application into review. */
export function startApplicationReview(id: string): Promise<ApplicationTransitionResult> {
  return authedFetch<ApplicationTransitionResult>(
    `/api/v1/admin/applications/${encodeURIComponent(id)}/start-review`,
    { method: 'POST' },
    'Could not start review.',
  );
}

/** Decline an application (terminal). */
export function declineApplication(id: string): Promise<ApplicationTransitionResult> {
  return authedFetch<ApplicationTransitionResult>(
    `/api/v1/admin/applications/${encodeURIComponent(id)}/decline`,
    { method: 'POST' },
    'Could not decline this application.',
  );
}

/** Approve an application — converts it into a Partner + draft Campaign. */
export function approveApplication(id: string): Promise<ApplicationTransitionResult> {
  return authedFetch<ApplicationTransitionResult>(
    `/api/v1/admin/applications/${encodeURIComponent(id)}/approve`,
    { method: 'POST' },
    'Could not approve this application.',
  );
}

// --- campaign review workflow ------------------------------------------------

export interface AllowedTransitions {
  campaign_id: string;
  status: string;
  allowed_transitions: string[];
}

export function listCampaignQueue(): Promise<CampaignRead[]> {
  return authedFetch<CampaignRead[]>(
    '/api/v1/admin/campaigns/queue',
    { method: 'GET' },
    'Could not load the campaign queue.',
  );
}

/** List ALL campaigns (staff), optionally filtered by status — so in-flight /
 *  live campaigns aren't orphaned after they leave the review queue. */
export function listAllCampaigns(statusFilter?: string): Promise<CampaignRead[]> {
  const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
  return authedFetch<CampaignRead[]>(
    `/api/v1/campaigns${q}`,
    { method: 'GET' },
    'Could not load campaigns.',
  );
}

export function getCampaign(id: string): Promise<CampaignRead> {
  // The staff-gated self-serve read works for any campaign (staff is unscoped).
  return authedFetch<CampaignRead>(
    `/api/v1/partner/campaigns/${encodeURIComponent(id)}`,
    { method: 'GET' },
    'Could not load this campaign.',
  );
}

export function getAllowedTransitions(id: string): Promise<AllowedTransitions> {
  return authedFetch<AllowedTransitions>(
    `/api/v1/admin/campaigns/${encodeURIComponent(id)}/allowed-transitions`,
    { method: 'GET' },
    'Could not load the campaign status.',
  );
}

/** Maps a state-machine verb to its REST path segment. */
const VERB_PATH: Record<string, string> = {
  start_review: 'start-review',
  approve: 'approve',
  reject: 'reject',
  begin_setup: 'begin-setup',
  go_live: 'go-live',
  end: 'end',
  cancel: 'cancel',
};

/** Operator verbs the admin console exposes (submit is the partner's action). */
export const OPERATOR_VERBS = Object.keys(VERB_PATH);

export const VERB_LABELS: Record<string, string> = {
  start_review: 'Start review',
  approve: 'Approve',
  reject: 'Reject',
  begin_setup: 'Begin setup',
  go_live: 'Go live',
  end: 'End',
  cancel: 'Cancel',
};

/** Verbs that need a confirm step before firing. */
export const DESTRUCTIVE_VERBS = new Set(['reject', 'cancel', 'go_live', 'end']);

export function applyTransition(id: string, verb: string): Promise<unknown> {
  const seg = VERB_PATH[verb];
  if (!seg) throw new ApiError(`Unknown action "${verb}".`, 400);
  return authedFetch<unknown>(
    `/api/v1/admin/campaigns/${encodeURIComponent(id)}/${seg}`,
    { method: 'POST' },
    `Could not ${VERB_LABELS[verb] ?? verb}.`,
  );
}

// --- lead consent gate (go-live prerequisite when lead capture is on) --------

/** Record the operator attestation that lead consent is in place (unblocks go-live). */
export function recordLeadConsent(id: string): Promise<CampaignRead> {
  return authedFetch<CampaignRead>(
    `/api/v1/admin/campaigns/${encodeURIComponent(id)}/record-lead-consent`,
    { method: 'POST' },
    'Could not record lead consent.',
  );
}

/** Clear the lead-consent attestation (re-blocks go-live). */
export function revokeLeadConsent(id: string): Promise<CampaignRead> {
  return authedFetch<CampaignRead>(
    `/api/v1/admin/campaigns/${encodeURIComponent(id)}/revoke-lead-consent`,
    { method: 'POST' },
    'Could not revoke lead consent.',
  );
}

// --- review checklist --------------------------------------------------------

/** The 7 sign-off items the operator must confirm before a campaign can be approved. */
export const CHECKLIST_ITEMS: { key: keyof ReviewChecklist; label: string }[] = [
  { key: 'brand_fit_ok', label: 'Brand fit' },
  { key: 'prize_legitimacy_ok', label: 'Prize is legitimate' },
  { key: 'prize_availability_ok', label: 'Prize is available' },
  { key: 'prize_value_ok', label: 'Prize value is accurate' },
  { key: 'feasibility_ok', label: 'Campaign is feasible' },
  { key: 'compliance_ok', label: 'Compliance checked' },
  { key: 'fulfilment_ok', label: 'Fulfilment plan in place' },
];

export interface ReviewChecklist {
  brand_fit_ok: boolean;
  prize_legitimacy_ok: boolean;
  prize_availability_ok: boolean;
  prize_value_ok: boolean;
  feasibility_ok: boolean;
  compliance_ok: boolean;
  fulfilment_ok: boolean;
}

export interface CampaignReview extends ReviewChecklist {
  id: string;
  campaign_id: string;
  reviewer_id: string | null;
  decision: string;
  notes: string | null;
}

/** Read the current review (checklist state). Rejects with 404 if none yet. */
export function getReview(id: string): Promise<CampaignReview> {
  return authedFetch<CampaignReview>(
    `/api/v1/admin/campaigns/${encodeURIComponent(id)}/review`,
    { method: 'GET' },
    'Could not load the review checklist.',
  );
}

/** Save the review checklist (partial allowed). */
export function updateReviewChecklist(
  id: string,
  payload: Partial<ReviewChecklist> & { notes?: string },
): Promise<CampaignReview> {
  return authedFetch<CampaignReview>(
    `/api/v1/admin/campaigns/${encodeURIComponent(id)}/review`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    'Could not save the review checklist.',
  );
}

// --- agreement record (epic-7) -----------------------------------------------

export interface AgreementRead {
  id: string;
  status: string;
  partner_id?: string | null;
  template_version?: string | null;
  document_ref?: string | null;
  signatory_name?: string | null;
  signatory_email?: string | null;
  signed_at?: string | null;
  created_at?: string | null;
}

export interface AgreementRecordPayload {
  template_version: string;
  document_ref: string;
  signatory_name: string;
  signatory_email: string;
  signed_at: string;
}

export function getAgreement(campaignId: string): Promise<AgreementRead> {
  return authedFetch<AgreementRead>(
    `/api/v1/admin/campaigns/${encodeURIComponent(campaignId)}/agreement`,
    { method: 'GET' },
    'Could not load the agreement.',
  );
}

export function recordAgreement(
  campaignId: string,
  payload: AgreementRecordPayload,
): Promise<AgreementRead> {
  return authedFetch<AgreementRead>(
    `/api/v1/admin/campaigns/${encodeURIComponent(campaignId)}/agreement`,
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not record the agreement.',
  );
}

export interface SetupConfig {
  id?: string;
  qr_redirect_url: string | null;
  prize_media_url: string | null;
}

// corp-14-5/7: operator-only — external QR redirect URL + prize image URL.
export function updateSetupConfig(
  campaignId: string,
  payload: { qr_redirect_url?: string | null; prize_media_url?: string | null },
): Promise<SetupConfig> {
  return authedFetch<SetupConfig>(
    `/api/v1/admin/campaigns/${encodeURIComponent(campaignId)}/setup-config`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    'Could not save the campaign setup config.',
  );
}

export function voidAgreement(campaignId: string): Promise<AgreementRead> {
  return authedFetch<AgreementRead>(
    `/api/v1/admin/campaigns/${encodeURIComponent(campaignId)}/agreement/void`,
    { method: 'POST' },
    'Could not void the agreement.',
  );
}

// --- deal workspace & creative library (v2-1) --------------------------------

export type MediaType = 'pdf' | 'video' | 'image' | 'audio';
export type DesignVersion = 'v1' | 'v2';
export type Orientation = '16x9' | '9x16';
export type ProposalStatus = 'draft' | 'sent' | 'won' | 'lost';

/** How a CreativeAsset was produced (v3-5). ``manual`` = seeded/uploaded, no spec. */
export type GenerationKind = 'pdf' | 'video' | 'image' | 'voiceover' | 'manual';

export interface CreativeAsset {
  id: string;
  client_slug: string;
  client_name: string;
  title: string;
  media_type: MediaType;
  format_label: string;
  gcs_url: string;
  design_version: DesignVersion;
  orientation: Orientation | null;
  created_at: string;
  // v3-5 corrections & versioning loop.
  generation_kind?: GenerationKind | null;
  // The exact request that produced this asset — used to PREFILL the "Edit &
  // regenerate" form. Null for ``manual`` (seeded/uploaded) assets.
  generation_spec?: Record<string, unknown> | null;
  lineage_id?: string | null;
  version?: number;
  is_current?: boolean;
  // v5-1-6 — auto-thumbnail / poster override. Null means "auto" (browser default).
  poster_url?: string | null;
}

export interface ProposalItem {
  id: string;
  label: string;
  url: string;
  media_type: MediaType;
  sort_order: number;
}

export interface Proposal {
  id: string;
  public_token: string;
  client_name: string;
  campaign_title: string;
  intro_md: string | null;
  status: ProposalStatus;
  created_by_staff_id: string | null;
  items: ProposalItem[];
  created_at: string;
  updated_at: string;
}

export interface ProposalListItem {
  id: string;
  public_token: string;
  client_name: string;
  campaign_title: string;
  status: ProposalStatus;
  item_count: number;
  created_at: string;
  updated_at: string;
}

export interface NewProposalItem {
  label: string;
  url: string;
  media_type: MediaType;
  sort_order?: number;
}

export interface NewProposal {
  client_name: string;
  campaign_title: string;
  intro_md?: string;
  status?: ProposalStatus;
  items?: NewProposalItem[];
}

export function listCreativeAssets(filters?: {
  client_slug?: string;
  media_type?: MediaType;
  design_version?: DesignVersion;
}): Promise<CreativeAsset[]> {
  const params = new URLSearchParams();
  if (filters?.client_slug) params.set('client_slug', filters.client_slug);
  if (filters?.media_type) params.set('media_type', filters.media_type);
  if (filters?.design_version) params.set('design_version', filters.design_version);
  const q = params.toString() ? `?${params.toString()}` : '';
  return authedFetch<CreativeAsset[]>(
    `/api/v1/admin/creative-assets${q}`,
    { method: 'GET' },
    'Could not load the creative library.',
  );
}

// --- Render tier toggle (v5-3) ---------------------------------------------
export interface RenderTierState {
  default_tier: 'cpu' | 'gpu';
  gpu_available: boolean;
  tiers: string[];
}

export function getRenderTier(): Promise<RenderTierState> {
  return authedFetch<RenderTierState>(
    '/api/v1/admin/render-tier',
    { method: 'GET' },
    'Could not load the render tier.',
  );
}

export function setRenderTier(tier: 'cpu' | 'gpu'): Promise<RenderTierState> {
  return authedFetch<RenderTierState>(
    '/api/v1/admin/render-tier',
    { method: 'PUT', body: JSON.stringify({ tier }) },
    'Could not update the render tier.',
  );
}

export function listProposals(status?: ProposalStatus): Promise<ProposalListItem[]> {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return authedFetch<ProposalListItem[]>(
    `/api/v1/admin/proposals${q}`,
    { method: 'GET' },
    'Could not load proposals.',
  );
}

export function getProposal(id: string): Promise<Proposal> {
  return authedFetch<Proposal>(
    `/api/v1/admin/proposals/${encodeURIComponent(id)}`,
    { method: 'GET' },
    'Could not load this proposal.',
  );
}

export function createProposal(payload: NewProposal): Promise<Proposal> {
  return authedFetch<Proposal>(
    '/api/v1/admin/proposals',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not create the proposal.',
  );
}

export function updateProposal(
  id: string,
  payload: Partial<Pick<NewProposal, 'client_name' | 'campaign_title' | 'intro_md'>> & {
    status?: ProposalStatus;
  },
): Promise<Proposal> {
  return authedFetch<Proposal>(
    `/api/v1/admin/proposals/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    'Could not update the proposal.',
  );
}

export function deleteProposal(id: string): Promise<void> {
  return authedFetch<void>(
    `/api/v1/admin/proposals/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    'Could not delete the proposal.',
  );
}

/** Delete a creative-library asset (row + best-effort GCS object). */
export function deleteCreativeAsset(id: string): Promise<void> {
  return authedFetch<void>(
    `/api/v1/admin/creative-assets/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    'Could not delete the asset.',
  );
}

export function addProposalItem(
  proposalId: string,
  item: NewProposalItem,
): Promise<Proposal> {
  return authedFetch<Proposal>(
    `/api/v1/admin/proposals/${encodeURIComponent(proposalId)}/items`,
    { method: 'POST', body: JSON.stringify(item) },
    'Could not attach the item.',
  );
}

export function removeProposalItem(
  proposalId: string,
  itemId: string,
): Promise<void> {
  return authedFetch<void>(
    `/api/v1/admin/proposals/${encodeURIComponent(proposalId)}/items/${encodeURIComponent(itemId)}`,
    { method: 'DELETE' },
    'Could not remove the item.',
  );
}

/** Build the absolute private share link for a proposal token. */
export function proposalShareUrl(token: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/p/${token}`;
}

// --- Pitch Studio: generate a pitch PDF (v3-1) -------------------------------

export interface BrochureCard {
  icon?: string;
  t: string;
  d: string;
}

export interface BrochureStep {
  t: string;
  d: string;
}

export interface BrochureStat {
  num: string;
  lbl: string;
}

export interface BrochureObjection {
  q: string;
  a: string;
}

/** The typed brochure spec the generate-pdf endpoint accepts. */
export interface GeneratePitchPdf {
  client_name: string;
  client_slug: string;
  filename: string;
  title: string;
  subtitle?: string;
  kicker?: string;
  prize_value: string;
  prize_label?: string;
  prize_icon?: string;
  illustration?: string | null;
  hero_image_url?: string | null;
  hero_badge?: string | null;
  hero_caption?: string | null;
  cover_tags?: string[];
  vp_headline: string;
  vp_lead: string;
  vp_cards: BrochureCard[];
  vp_steps: BrochureStep[];
  prize_head: string;
  prize_text: string;
  prize_pills?: string[];
  stats: BrochureStat[];
  risk_reversal_t: string;
  risk_reversal_d: string;
  objections: BrochureObjection[];
  close_headline: string;
  close_sub: string;
  close_cta?: string;
  close_meta?: string;
  close_compliance?: string;
}

/** The newly-created CreativeAsset (the generated PDF in the library). */
export interface GeneratedPdf extends CreativeAsset {
  pages: number;
}

export function generatePitchPdf(payload: GeneratePitchPdf): Promise<GeneratedPdf> {
  return authedFetch<GeneratedPdf>(
    '/api/v1/admin/pitch-studio/generate-pdf',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not generate the pitch PDF.',
  );
}

// --- Pitch Studio: generate a video (ASYNC, v3-2) ----------------------------

export type VideoType = 'pitch' | 'tvad';
export type VideoRenderStatus = 'pending' | 'rendering' | 'done' | 'failed';

/** The typed params the async generate-video endpoint accepts. */
export interface GeneratePitchVideo {
  video_type: VideoType;
  client_name: string;
  client_slug: string;
  filename: string;
  dealer_name: string;
  vehicle: string;
  prize_value: number;
  // Pitch-only optionals.
  catchment?: string | null;
  hero_image_url?: string | null;
  prize_value_spoken?: string | null;
  // TV-ad-only optional.
  url?: string | null;
}

/** A video render job (polled until terminal). On done the asset is included. */
export interface VideoRenderJob {
  id: string;
  status: VideoRenderStatus;
  video_type: VideoType;
  gcs_url: string | null;
  error: string | null;
  creative_asset_id: string | null;
  creative_asset: CreativeAsset | null;
  created_at: string;
  updated_at: string;
}

export function generatePitchVideo(payload: GeneratePitchVideo): Promise<VideoRenderJob> {
  return authedFetch<VideoRenderJob>(
    '/api/v1/admin/pitch-studio/generate-video',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not start the video render.',
  );
}

export function getVideoJob(jobId: string): Promise<VideoRenderJob> {
  return authedFetch<VideoRenderJob>(
    `/api/v1/admin/pitch-studio/jobs/${encodeURIComponent(jobId)}`,
    { method: 'GET' },
    'Could not load the render job.',
  );
}

// --- Pitch Studio: generate a hero image (SYNCHRONOUS, v3-3) -----------------

export type ImageAspect = '16:9' | '1:1' | '9:16';

/** The typed params the synchronous generate-image endpoint accepts. */
export interface GeneratePitchImage {
  client_name: string;
  client_slug: string;
  filename: string;
  prompt: string;
  aspect: ImageAspect;
}

/**
 * Generate a photoreal hero image (gpt-image-1). This is SYNCHRONOUS — the
 * backend makes the OpenAI call + GCS upload in-request (~10-30s) and returns the
 * new CreativeAsset (media_type=image), which lands in the Creative Library and is
 * reusable as the hero image URL for the PDF + video generators.
 */
export function generatePitchImage(
  payload: GeneratePitchImage,
): Promise<CreativeAsset> {
  return authedFetch<CreativeAsset>(
    '/api/v1/admin/pitch-studio/generate-image',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not generate the image.',
  );
}

// --- Pitch Studio: generate a voiceover (SYNCHRONOUS, v3-4) ------------------

/** The TTS voices the generate-voiceover endpoint accepts. */
export type Voice = 'ash' | 'nova' | 'onyx' | 'sage' | 'alloy' | 'shimmer';

/** The typed params the synchronous generate-voiceover endpoint accepts. */
export interface GeneratePitchVoiceover {
  client_name: string;
  client_slug: string;
  filename: string;
  text: string;
  voice: Voice;
  instructions?: string;
}

/**
 * Generate a voiceover (gpt-4o-mini-tts). This is SYNCHRONOUS — the backend makes
 * the OpenAI TTS call + GCS upload in-request (~5-15s) and returns the new
 * CreativeAsset (media_type=audio), which lands in the Creative Library with an
 * inline <audio controls> player.
 */
export function generatePitchVoiceover(
  payload: GeneratePitchVoiceover,
): Promise<CreativeAsset> {
  return authedFetch<CreativeAsset>(
    '/api/v1/admin/pitch-studio/generate-voiceover',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not generate the voiceover.',
  );
}

// --- Pitch Studio: corrections & versioning loop (v3-5) ----------------------

/** A single version within a lineage (a versioning-aware CreativeAsset). */
export interface AssetVersion extends CreativeAsset {
  version: number;
  is_current: boolean;
  lineage_id: string | null;
  generation_kind: GenerationKind | null;
}

/** All versions of one logical asset, ordered oldest -> newest. */
export interface AssetLineage {
  lineage_id: string;
  current_version: number | null;
  versions: AssetVersion[];
}

/** The new version produced by /regenerate. For PDF it also carries ``pages``. */
export interface RegeneratedAsset extends AssetVersion {
  pages?: number;
}

/**
 * Edit & regenerate: re-run the source asset's generator with deep-merged
 * ``spec_overrides`` as a NEW version in the same lineage. For video the response
 * is the async render job (poll it; the new version lands via the callback).
 */
export function regenerateAsset(
  sourceAssetId: string,
  specOverrides: object,
): Promise<RegeneratedAsset | VideoRenderJob> {
  return authedFetch<RegeneratedAsset | VideoRenderJob>(
    '/api/v1/admin/pitch-studio/regenerate',
    {
      method: 'POST',
      body: JSON.stringify({
        source_asset_id: sourceAssetId,
        spec_overrides: specOverrides,
      }),
    },
    'Could not regenerate this asset.',
  );
}

/** Fetch every version of one logical asset (for the history drawer + compare). */
export function getLineage(lineageId: string): Promise<AssetLineage> {
  return authedFetch<AssetLineage>(
    `/api/v1/admin/pitch-studio/lineage/${encodeURIComponent(lineageId)}`,
    { method: 'GET' },
    'Could not load the version history.',
  );
}

/** Promote/revert: make this version current (flips its siblings false). */
export function setCurrentVersion(assetId: string): Promise<AssetVersion> {
  return authedFetch<AssetVersion>(
    `/api/v1/admin/pitch-studio/assets/${encodeURIComponent(assetId)}/set-current`,
    { method: 'POST' },
    'Could not set this version as current.',
  );
}

// --- Pitch Studio: one-click full pack (v3-6) --------------------------------

/** Which outputs to generate in a full pack. */
export interface PackInclude {
  pdf: boolean;
  image: boolean;
  voiceover: boolean;
  video_pitch: boolean;
  video_tvad: boolean;
}

/** The single form input that generates a client's WHOLE creative pack. */
export interface GeneratePack {
  client_name: string;
  client_slug: string;
  vehicle_or_prize: string;
  prize_value: number;
  hero_image_url?: string | null;
  include: PackInclude;
  create_proposal: boolean;
  intro?: string;
}

/** Per-output result for one SYNC generator in the pack. */
export interface PackAssetResult {
  output: 'pdf' | 'image' | 'voiceover';
  status: 'done' | 'failed';
  asset_id?: string | null;
  gcs_url?: string | null;
  media_type?: MediaType | null;
  lineage_id?: string | null;
  version?: number | null;
  error?: string | null;
  error_status?: number | null;
}

/** Per-output result for one ASYNC video kick in the pack. */
export interface PackVideoJobResult {
  output: 'video_pitch' | 'video_tvad';
  status: 'kicked' | 'failed';
  job_id?: string | null;
  error?: string | null;
  error_status?: number | null;
}

/** The draft proposal assembled from the pack (if requested). */
export interface PackProposalResult {
  id: string;
  public_token: string;
  client_name: string;
  campaign_title: string;
  status: ProposalStatus;
  item_count: number;
  videos_pending: boolean;
}

/** The full pack summary: per-output status for every requested output. */
export interface GeneratePackResponse {
  client_name: string;
  client_slug: string;
  assets: PackAssetResult[];
  video_jobs: PackVideoJobResult[];
  proposal: PackProposalResult | null;
}

/**
 * Generate a client's WHOLE creative pack from one form. Runs the requested SYNC
 * generators (image/pdf/voiceover) in-request, KICKS the requested ASYNC video
 * job(s), and (optionally) assembles a draft proposal. Partial-failure safe — one
 * failed output is reported in the structured result, not surfaced as an error.
 */
export function generatePack(payload: GeneratePack): Promise<GeneratePackResponse> {
  return authedFetch<GeneratePackResponse>(
    '/api/v1/admin/pitch-studio/generate-pack',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not generate the pack.',
  );
}

// --- Pitch Studio: generate a whole client pack from a manifest (v5-4) --------
//
// One staff-gated call takes a full ClientManifest (identity + scenes + business +
// brand + which formats) and produces the client's entire pack: the requested
// videos (rendered in the background), a branded PDF brochure, and a draft
// proposal stitched together. Every image field MUST be a
// https://storage.googleapis.com/... URL — the backend rejects non-GCS URLs.

/** The industry presets the manifest generator recognises. */
export type ManifestIndustry =
  | 'dental'
  | 'medical'
  | 'cosmetic-surgery'
  | 'automotive'
  | 'media-broadcast'
  | 'retail'
  | 'generic';

/** The output formats a pack can include — a subset (default all four). */
export type ManifestFormat = 'pitchad' | 'pitchadv' | 'showcase' | 'pdf_brochure';

/** All four pack formats, in canonical order — the default selection. */
export const MANIFEST_FORMATS: ManifestFormat[] = [
  'pitchad',
  'pitchadv',
  'showcase',
  'pdf_brochure',
];

/** Optional per-tenant brand palette. All hex strings (e.g. "#22d3ee"). */
export interface ManifestBrand {
  primary: string;
  accent: string;
  warm: string;
  ink: string;
  muted: string;
}

/** The six (one optional) scene background image URLs. All GCS https URLs. */
export interface ManifestScenes {
  hero: string;
  create: string;
  conv: string;
  browse: string;
  winner: string;
  handover?: string;
}

/** The sponsor business block shown on the brochure + winner scene. */
export interface ManifestBusiness {
  image: string;
  address: string;
  rating?: string;
  reviews?: string;
}

/**
 * The full client manifest the generate-pack-from-manifest endpoint accepts.
 * Mirrors the backend ClientManifest model. ALL image fields (scenes.*,
 * page_image, qr_image, prize_image, business.image) must be
 * https://storage.googleapis.com/... URLs.
 */
export interface ClientManifest {
  slug: string;
  sponsor_name: string;
  industry: ManifestIndustry;
  prize_label: string;
  prize_short: string;
  prize_spoken?: string;
  prize_value_label?: string;
  url: string;
  register_url: string;
  brand?: ManifestBrand;
  scenes: ManifestScenes;
  page_image: string;
  qr_image: string;
  prize_image?: string;
  business: ManifestBusiness;
  /**
   * Per-deal prize-funding model (v6-1-9). Drives the pack's funding copy.
   * Omit → backend defaults to `we_fund` (the operator-funded "we pay" model).
   */
  sponsorship_type?: SponsorshipModel;
  /**
   * When true, the pack also carries a "Ways to work together" page (PDF) and an
   * openness line on the video Partnership scene, presenting the models we're open
   * to alongside the primary one. Omit → false (backward compatible).
   */
  open_to_partnership_models?: boolean;
  /** Subset of the four formats. Omit / empty → backend defaults to all four. */
  formats?: ManifestFormat[];
}

/** Per-deal prize-funding model. Mirrors the backend `SponsorshipModel` enum. */
export type SponsorshipModel =
  | 'we_fund'
  | 'fully_sponsored'
  | 'discounted'
  | 'co_funded'
  | 'rented_platform'
  | 'profit_split';

/** The proposal stitched together from the generated pack. */
export interface ManifestProposalResult {
  id: string;
  public_token: string;
  share_path: string;
  client_name: string;
  campaign_title: string;
  status: ProposalStatus;
  item_count: number;
  videos_pending: boolean;
}

/** The PDF brochure result (synchronous — generated in-request). */
export interface ManifestPdfResult {
  status: 'done' | 'failed' | string;
  asset_id?: string | null;
  gcs_url?: string | null;
  pages?: number | null;
  error?: string | null;
}

/** One video kick within the pack (rendered asynchronously). */
export interface ManifestVideoResult {
  format: ManifestFormat | string;
  video_type: string;
  status: 'kicked' | 'queued' | 'failed' | string;
  job_id?: string | null;
  final_gcs_url?: string | null;
  error?: string | null;
}

/** The 201 success body for generate-pack-from-manifest. */
export interface GeneratePackFromManifestResponse {
  slug: string;
  sponsor_name: string;
  proposal: ManifestProposalResult;
  pdf: ManifestPdfResult;
  videos: ManifestVideoResult[];
  note?: string | null;
}

/** One format's missing-asset list, from a 422 incomplete-manifest response. */
export interface ManifestMissingEntry {
  format: string;
  missing: string[];
}

/**
 * Generate a whole client pack (videos + PDF + proposal) from a ClientManifest.
 * On a 422 the thrown ApiError carries the raw body in `.detail`; callers can
 * read `detail.detail.missing` (a `ManifestMissingEntry[]`) to show the operator
 * exactly which asset slot is missing per format.
 */
export function generatePackFromManifest(
  manifest: ClientManifest,
): Promise<GeneratePackFromManifestResponse> {
  return authedFetch<GeneratePackFromManifestResponse>(
    '/api/v1/admin/pitch-studio/generate-pack-from-manifest',
    { method: 'POST', body: JSON.stringify(manifest) },
    'Could not generate the pack from the manifest.',
  );
}

/**
 * Pull the per-format missing-asset list out of a 422 ApiError raised by
 * `generatePackFromManifest`. Returns null for any other shape (so callers fall
 * back to the plain error message).
 */
export function manifestMissing(err: unknown): ManifestMissingEntry[] | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  const body = err.detail;
  if (!body || typeof body !== 'object') return null;
  const detail = (body as { detail?: unknown }).detail;
  if (!detail || typeof detail !== 'object') return null;
  const missing = (detail as { missing?: unknown }).missing;
  if (!Array.isArray(missing)) return null;
  return missing.filter(
    (m): m is ManifestMissingEntry =>
      !!m &&
      typeof m === 'object' &&
      typeof (m as ManifestMissingEntry).format === 'string' &&
      Array.isArray((m as ManifestMissingEntry).missing),
  );
}

// --- consented leads (epic-5, leads-pii) -------------------------------------
//
// Partner-scoped, consent-gated lead export. The backend returns ONLY leads with
// recorded, un-withdrawn consent for the campaign; contact PII (name/email/phone)
// is decrypted server-side and present only for those leads. Every access is
// audited. The CSV download hits the same gate + audit.

/** One consented lead — opaque token + consent provenance + (consented) PII. */
export interface LeadExportItem {
  lead_id: string;
  participant_token: string;
  source: string;
  captured_at: string;
  consent_text_version: string;
  consent_scope: string;
  consent_at?: string | null;
  // Decrypted contact PII — null when no contact details were captured.
  name?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface LeadExportResponse {
  campaign_id: string;
  partner_id: string;
  count: number;
  leads: LeadExportItem[];
}

/** Fetch the consented leads (incl. decrypted PII) for one campaign. */
export function getCampaignLeads(campaignId: string): Promise<LeadExportResponse> {
  return authedFetch<LeadExportResponse>(
    `/api/v1/admin/campaigns/${encodeURIComponent(campaignId)}/leads`,
    { method: 'GET' },
    'Could not load the consented leads for this campaign.',
  );
}

/**
 * Download the consented-leads CSV for a campaign, with the staff Bearer token
 * attached (so it stays behind the same auth + consent gate + audit). Triggers a
 * browser download via an object URL. Throws ApiError on a non-2xx response.
 */
export async function downloadCampaignLeadsCsv(campaignId: string): Promise<void> {
  const url = `/api/v1/admin/campaigns/${encodeURIComponent(campaignId)}/leads.csv`;
  const token = getToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let resp: Response;
  try {
    resp = await fetch(url, { method: 'GET', headers });
  } catch (e) {
    throw new ApiError(
      'We could not reach the server. Please check your connection and try again.',
      0,
      e,
    );
  }
  if (resp.status === 401) {
    // Mirror authedFetch: a stale token must bounce to login, not silent-fail.
    window.location.assign('/admin/login');
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }
  if (!resp.ok) {
    throw new ApiError('Could not download the leads CSV.', resp.status);
  }

  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `leads-${campaignId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Set (or update) the poster / thumbnail frame for a video creative asset.
 * Instructs the backend to extract the frame at `timestampSeconds` from the
 * video and store it as a GCS image, then patch `poster_url` on the asset row.
 *
 * @param assetId         - The video CreativeAsset.id to update.
 * @param timestampSeconds - The video playback position (seconds) to capture.
 * @returns The updated CreativeAsset, including the new `poster_url`.
 */
export function setVideoPoster(
  assetId: string,
  timestampSeconds: number,
): Promise<CreativeAsset> {
  return authedFetch<CreativeAsset>(
    `/api/v1/admin/pitch-studio/video/${encodeURIComponent(assetId)}/poster`,
    { method: 'POST', body: JSON.stringify({ timestamp_seconds: timestampSeconds }) },
    'Could not set the video thumbnail.',
  );
}

/**
 * Force-download a Creative Library asset via the same-origin staff proxy
 * (Content-Disposition: attachment). authedFetch can't stream a blob, so we
 * fetch with the Bearer token directly, then trigger a blob download — a real
 * save dialog instead of the file opening in a tab.
 */
export async function downloadCreativeAsset(assetId: string, filename: string): Promise<void> {
  const resp = await fetch(`/api/v1/admin/creative-assets/${encodeURIComponent(assetId)}/download`, {
    headers: { Authorization: `Bearer ${getToken() ?? ''}` },
  });
  if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
